import { Navigate, createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { useGetOpportunity, useListMySubscriptions } from '@eureca/candidate-api-sdk/hooks';
import { HomeGate } from '@/features/account/shell/HomeGate';
import { ensureOnboardingProgressLoaded } from '@/features/account/onboarding/shared/ensure-onboarding-progress.loader';
import {
  isActivelyEnrolledInJob,
  isJobApplicationWindowClosed,
} from '@/features/subscription/shared/subscription-status';

/**
 * FAS-Inscrição RN-01/02 — gate de "uma inscrição por programa".
 *
 * Antes de redirecionar pro formulário, checa em paralelo a vaga-alvo
 * (`useGetOpportunity` → `programId`) e as subs do candidato
 * (`useListMySubscriptions`). Se houver subscription ativa (não
 * `cancelled`, não terminal) no MESMO `programId` da vaga-alvo, mas em
 * vaga **diferente**, redireciona para a tela explicativa
 * `programa-em-andamento`; caso contrário, segue para o `formulario`.
 *
 * Forward-compat com Core: enquanto `programId` não vier em
 * `/users/me/subscriptions`, o match é falsy e o candidato vê o fluxo
 * normal — o 409 PROGRAM_SWITCH_REQUIRED no apply ainda protege a UX no
 * final.
 *
 * Search param `?bypassDuplicate=1` curto-circuita o gate quando o
 * candidato clica "Fazer inscrição nessa vaga" na tela explicativa
 * (RN-03), evitando loop de redirect.
 */
function InscriptionIndex() {
  const { jobId } = Route.useParams();
  const { bypassDuplicate } = Route.useSearch();

  const opportunityQuery = useGetOpportunity(jobId, {
    query: { enabled: Boolean(jobId) && bypassDuplicate !== 1 },
  });
  const subscriptionsQuery = useListMySubscriptions(undefined, {
    query: { enabled: bypassDuplicate !== 1 },
  });

  // Bypass explícito (RN-03) ou queries ainda carregando → segue direto.
  // Default conservador: sem informação suficiente, deixa o apply final
  // gatear via 409. UX prefere "passar adiante e checar no submit" a
  // segurar o candidato na tela inicial.
  if (bypassDuplicate === 1) {
    return <Navigate to="/vagas/$jobId/inscricao/formulario" params={{ jobId }} replace />;
  }

  if (opportunityQuery.isLoading || subscriptionsQuery.isLoading) {
    // Brief skeleton — o redirect é quase imediato em cache quente.
    return (
      <div style={{ padding: '2rem', fontFamily: 'system-ui' }}>
        <p>Carregando…</p>
      </div>
    );
  }

  // Já inscrito NESTA vaga (deep-link / corrida de cache) → manda direto pras
  // etapas, espelhando a proteção de dupla inscrição da API (POST /apply → 409)
  // e o CTA adaptado da página de informações da vaga.
  if (isActivelyEnrolledInJob(subscriptionsQuery.data?.items, jobId)) {
    return <Navigate to="/vagas/$jobId/processo" params={{ jobId }} replace />;
  }

  const opportunity = opportunityQuery.data as
    | { programId?: string | null; endApplying?: string | null }
    | undefined;
  const targetProgramId = opportunity?.programId ?? null;

  // Elegibilidade por prazo: a vaga espelha o ciclo do programa. Se a janela de
  // inscrição (endApplying = data de FIM do programa) já encerrou, não inicia o
  // fluxo — devolve à página da vaga (normaliza PRIV-004, link privado expirado).
  if (isJobApplicationWindowClosed(opportunity?.endApplying)) {
    return <Navigate to="/vagas/$jobId" params={{ jobId }} replace />;
  }

  const hasActiveDuplicate = (() => {
    if (!targetProgramId) return false;
    const items = subscriptionsQuery.data?.items ?? [];
    return items.some((sub) => {
      if (sub.cancelled) return false;
      if (sub.jobId === jobId) return false;
      const status = (sub.enrollmentStatus ?? '').toLowerCase().trim();
      if (status === 'closed' || status === 'closed_by_consultant' || status === 'expired') {
        return false;
      }
      const subProgramId = (sub as { programId?: string | null }).programId ?? null;
      return subProgramId === targetProgramId;
    });
  })();

  if (hasActiveDuplicate) {
    return (
      <Navigate to="/vagas/$jobId/inscricao/programa-em-andamento" params={{ jobId }} replace />
    );
  }

  return <Navigate to="/vagas/$jobId/inscricao/formulario" params={{ jobId }} replace />;
}

/**
 * `returnTo` na vaga garante que um candidato que chegou aqui via login/cadastro
 * ("Fazer inscrição" deslogado) e ainda tem onboarding pendente seja devolvido à
 * inscrição ao concluir o onboarding (em vez do `/perfil` default).
 */
function InscriptionIndexGate() {
  const { jobId } = Route.useParams();
  return (
    <HomeGate returnTo={`/vagas/${jobId}/inscricao`}>
      <InscriptionIndex />
    </HomeGate>
  );
}

/** Search schema — `bypassDuplicate=1` vem da tela explicativa (RN-03). */
const inscriptionIndexSearchSchema = z.object({
  bypassDuplicate: z.literal(1).optional(),
});

export const Route = createFileRoute('/vagas/$jobId_/inscricao/')({
  validateSearch: inscriptionIndexSearchSchema,
  component: InscriptionIndexGate,
  loader: ({ context }) => ensureOnboardingProgressLoaded(context),
});
